import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, Button } from 'src/common';
import { Paths } from 'src/pages/paths';
import styles from '../index.module.scss';
import { IUserName } from 'src/models/users/types';
import { useInviteUsers, IInvitedUser } from 'src/models/company/Users/hooks';
import classNames from 'classnames';
import { Link, useNavigate } from 'react-router-dom';
import { useDefaultPremiumRoles } from 'src/models/users/Roles/hooks';

interface IFormState {
  users: Pick<IUserName, 'email'>[];
}

export const InviteTeamPage: FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [addCompanyEmployees, { loading: isLoading }] = useInviteUsers();

  const defaultRoles = useDefaultPremiumRoles();

  const initialValues = useMemo((): IFormState => {
    return {
      users: [...Array(3).keys()].map(() => {
        return { email: '' };
      })
    };
  }, []);

  const onInvite = useCallback(() => {
    form.resetFields();
    navigate(Paths.HOME_PATH);
  }, [form, navigate]);

  const onError = useCallback(
    (users: IInvitedUser[]) => {
      form.resetFields();
      form.setFields([
        { name: 'users', value: users.map(user => user.email), errors: [t('Auth.unInvitedUsersError')] }
      ]);
    },
    [form, t]
  );

  const onFinish = useCallback(
    (values: IFormState) => {
      const { users } = values;
      const invitedUsers = users
        .filter(user => !!user.email)
        .map(user => ({
          email: user.email,
          name: null,
          lastName: null,
          inviteSubUser: true,
          roles: defaultRoles
        }));
      !!invitedUsers.length && addCompanyEmployees({ users: invitedUsers, onInvite, onError });
    },
    [addCompanyEmployees, defaultRoles, onError, onInvite]
  );

  return (
    <>
      {!isLoading && (
        <Form form={form} initialValues={initialValues} onFinish={onFinish} layout={'vertical'} className={styles.form}>
          <div className={styles.heading}>
            <h3 className={styles.title}>
              <span>{t('Auth.invite.title')}</span>
            </h3>
            <span className={styles.desc}>{t('Auth.invite.desc')}</span>
          </div>
          <Form.List name={'users'}>
            {fields => {
              return (
                <>
                  {fields.map(field => {
                    return (
                      <Form.Item
                        label={`${t('Auth.emailAddress')} #${field.fieldKey + 1}`}
                        {...field}
                        fieldKey={[field.fieldKey, 'users']}
                        key={`${field.key}_email`}
                        className={classNames(styles.field, styles.emailField)}
                        name={[field.name, 'email']}
                        rules={[{ type: 'email', message: t('Common.incorrectEmailError') }]}
                      >
                        <Input data-type={'email'} type={'email'} placeholder={t('Auth.emailAddress')} />
                      </Form.Item>
                    );
                  })}
                </>
              );
            }}
          </Form.List>
          <div className={styles.actions}>
            <Button htmlType={'submit'} loading={isLoading} type={'primary'}>
              {t('Auth.invite.buttons.submit')}
            </Button>
            <Link to={Paths.HOME_PATH} className={styles.link}>
              {t('Auth.invite.buttons.skip')}
            </Link>
          </div>
        </Form>
      )}
    </>
  );
};

export default InviteTeamPage;
